import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import TopNav from "../components/TopNav.jsx";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Full Baker · master-only · spec → runnable starter code.
 *
 * Founder ruling 2026-08-11:
 *   "Baker-lite is like getting the initial app to build-stage with a prompt,
 *    and then Baker is the builder itself."
 *
 * Paste a scaffold_id (from Baker-lite recent queue) and Baker returns a
 * bundle of files (backend + frontend + README) ready to run.
 * Frontend then offers a client-side zip download.
 */
export default function BakerPage() {
  const [params] = useSearchParams();
  const initialId = params.get("scaffold_id") || "";
  const [scaffoldId, setScaffoldId] = useState(initialId);
  const [bundle, setBundle] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [scaffolds, setScaffolds] = useState([]);

  // Load recent scaffolds for a dropdown picker
  useEffect(() => {
    const token = localStorage.getItem("sage_master_token") || "";
    fetch(`${API}/api/sage/baker-lite/recent`, { headers: { "X-Sage-Master": token } })
      .then((r) => r.ok ? r.json() : { items: [] })
      .then((d) => setScaffolds(d.items || []))
      .catch(() => setScaffolds([]));
  }, []);

  const bake = async () => {
    if (!scaffoldId.trim()) { setErr("Paste or pick a scaffold id first."); return; }
    setBusy(true);
    setErr("");
    setBundle(null);
    setSelectedFile(null);
    const token = localStorage.getItem("sage_master_token") || "";
    try {
      const r = await fetch(`${API}/api/sage/baker/bake/${scaffoldId.trim()}`, {
        method: "POST",
        headers: { "X-Sage-Master": token },
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || `HTTP ${r.status}`);
      setBundle(d);
      if (d.files?.length) setSelectedFile(d.files[0].path);
    } catch (e) {
      setErr(String(e.message || e));
    } finally {
      setBusy(false);
    }
  };

  const downloadZip = () => {
    if (!bundle?.files) return;
    // Client-side plain concatenation — the operator can review each file inline
    // and then export by copy-paste OR use the individual "Download this file"
    // buttons. A real zip needs a zip library; we ship one file at a time.
    const combined = bundle.files.map((f) => `// ── ${f.path} ──\n${f.content}\n\n`).join("");
    const blob = new Blob([combined], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${bundle.app_name || "baker-bundle"}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadFile = (file) => {
    const blob = new Blob([file.content], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    // Flatten path into a safe filename
    a.download = file.path.replace(/\//g, "__");
    a.click();
    URL.revokeObjectURL(url);
  };

  const currentFile = bundle?.files?.find((f) => f.path === selectedFile);

  return (
    <div className="min-h-screen bg-sage-bg text-sage-text sage-grain relative">
      <TopNav />
      <div className="mx-auto max-w-6xl px-4 md:px-8 py-12">
        <div className="text-xs uppercase tracking-[0.2em] text-sage-muted mb-2">Baker · The Workshop</div>
        <h1 className="text-4xl md:text-5xl font-bold tracking-tighter uppercase mb-4" data-testid="baker-headline">
          Spec → runnable starter
        </h1>
        <p className="text-base text-sage-muted leading-relaxed max-w-3xl mb-8 sage-prose">
          Baker-lite drafted the spec · Baker turns it into files.
          FastAPI backend · React frontend · Mongo · README. Review inline, download the pieces you want.
        </p>

        <div className="mb-8 border border-sage-border p-6">
          <div className="text-[10px] uppercase tracking-[0.2em] text-sage-accent mb-3">Pick a spec from the queue</div>

          {scaffolds.length > 0 && (
            <select
              data-testid="baker-scaffold-picker"
              value={scaffoldId}
              onChange={(e) => setScaffoldId(e.target.value)}
              className="w-full border border-sage-border bg-sage-bg text-sage-text px-3 py-2 mb-3 focus:border-sage-accent outline-none"
            >
              <option value="">— pick a scaffold —</option>
              {scaffolds.map((s) => (
                <option key={s.scaffold_id} value={s.scaffold_id}>
                  {s.spec?.name || "(unnamed)"} · {s.scaffold_id.slice(0, 8)}…
                </option>
              ))}
            </select>
          )}

          <input
            data-testid="baker-scaffold-input"
            type="text"
            value={scaffoldId}
            onChange={(e) => setScaffoldId(e.target.value)}
            placeholder="…or paste a scaffold id"
            className="w-full border border-sage-border bg-sage-bg px-3 py-2 text-sage-text focus:border-sage-accent outline-none mb-3"
          />

          <button
            onClick={bake}
            disabled={busy || !scaffoldId.trim()}
            data-testid="baker-bake-submit"
            className="border-2 border-sage-accent bg-sage-accent text-sage-bg px-6 py-2 text-xs uppercase tracking-wider font-bold hover:bg-sage-text hover:border-sage-text disabled:opacity-50 transition-colors"
          >
            {busy ? "Baking…" : "Bake it →"}
          </button>

          {err && (
            <div className="mt-3 text-sm text-sage-warn" data-testid="baker-error">
              {err}
            </div>
          )}
        </div>

        {bundle && (
          <div data-testid="baker-bundle">
            <div className="flex justify-between items-center mb-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.15em] text-sage-muted">App</div>
                <div className="text-2xl font-bold uppercase tracking-tight text-sage-text" data-testid="baker-app-name">
                  {bundle.app_name} · {bundle.file_count} files
                </div>
              </div>
              <button
                onClick={downloadZip}
                data-testid="baker-download-bundle"
                className="border-2 border-sage-accent bg-sage-accent text-sage-bg px-4 py-1.5 text-[10px] uppercase tracking-wider font-bold hover:bg-sage-text hover:border-sage-text transition-colors"
              >
                Download bundle
              </button>
            </div>

            {bundle.run_notes?.length > 0 && (
              <div className="mb-6 border border-sage-accent/40 bg-sage-pale/20 p-4">
                <div className="text-[10px] uppercase tracking-[0.2em] text-sage-accent mb-2">Run notes</div>
                <ul className="text-sm text-sage-text sage-prose space-y-1">
                  {bundle.run_notes.map((n, i) => <li key={i}>· {n}</li>)}
                </ul>
              </div>
            )}

            <div className="grid md:grid-cols-4 gap-4">
              <div className="md:col-span-1 border border-sage-border p-3" data-testid="baker-file-list">
                <div className="text-[10px] uppercase tracking-[0.2em] text-sage-muted mb-2">Files</div>
                <div className="space-y-1">
                  {bundle.files?.map((f) => (
                    <button
                      key={f.path}
                      onClick={() => setSelectedFile(f.path)}
                      data-testid={`baker-file-${f.path.replace(/\//g, "__")}`}
                      className={`w-full text-left text-xs px-2 py-1 border ${selectedFile === f.path ? "border-sage-accent text-sage-accent" : "border-transparent text-sage-muted hover:text-sage-text"}`}
                    >
                      {f.path}
                    </button>
                  ))}
                </div>
              </div>

              <div className="md:col-span-3 border border-sage-border" data-testid="baker-file-viewer">
                {currentFile ? (
                  <div>
                    <div className="flex justify-between items-center border-b border-sage-border px-3 py-2 bg-sage-pale/10">
                      <div className="text-[10px] uppercase tracking-[0.15em] text-sage-muted font-mono">{currentFile.path}</div>
                      <button
                        onClick={() => downloadFile(currentFile)}
                        data-testid="baker-download-file"
                        className="text-[10px] uppercase tracking-[0.15em] text-sage-accent hover:text-sage-text"
                      >
                        Download
                      </button>
                    </div>
                    <pre className="text-xs text-sage-text p-3 overflow-x-auto max-h-[500px] whitespace-pre-wrap font-mono">
                      {currentFile.content}
                    </pre>
                  </div>
                ) : (
                  <div className="p-6 text-sage-muted text-sm">Pick a file on the left.</div>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="mt-16 border-t border-sage-border pt-8 text-sm text-sage-muted sage-prose">
          <p>
            Draft a new spec at <Link to="/baker-lite" className="text-sage-accent underline">Baker-lite</Link>.
            Browse the queue at <Link to="/baker-lite/recent" className="text-sage-accent underline">the master queue</Link>.
          </p>
        </div>
      </div>
    </div>
  );
}
